/**
 * Responsive column grids.
 *
 * A named set rather than arbitrary Tailwind, because the prototype's
 * breakpoints are not Tailwind's defaults and every screen must collapse at the
 * same widths. The steps are the mockup's: 4-up and detail become 2-up below
 * 1240px, everything becomes 1-up below 920px.
 *
 * `detail` is the asymmetric 1.55 : 1 split every entity detail page uses — a
 * wide main column beside a narrower sidebar.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.4, §4.5
 */
import { cn } from '../../lib/cn';

/** The available column layouts. */
export type GridCols = 2 | 3 | 4 | 'detail';

/* Arbitrary min-width variants rather than Tailwind's `lg:`/`xl:`, because the
   prototype collapses at 920px and 1240px and Tailwind's stops are 1024/1280.
   Using the nearest named breakpoint would move the collapse by 100px and the
   two-column detail layout would break at the wrong width. */
const GRID_COLS: Record<GridCols, string> = {
  2: 'grid-cols-1 min-[920px]:grid-cols-2',
  3: 'grid-cols-1 min-[920px]:grid-cols-2 min-[1240px]:grid-cols-3',
  4: 'grid-cols-1 min-[920px]:grid-cols-2 min-[1240px]:grid-cols-4',
  detail:
    'grid-cols-1 min-[920px]:grid-cols-2 min-[1240px]:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]',
};

export interface GridProps {
  cols: GridCols;
  children: React.ReactNode;
  className?: string;
}

export function Grid({ cols, children, className }: GridProps): React.JSX.Element {
  return <div className={cn('grid gap-s3', GRID_COLS[cols], className)}>{children}</div>;
}

export interface StackProps {
  children: React.ReactNode;
  className?: string;
}

/** A vertical stack at the standard gap — the grid's one-dimensional sibling. */
export function Stack({ children, className }: StackProps): React.JSX.Element {
  return <div className={cn('flex flex-col gap-s3', className)}>{children}</div>;
}
